import { AppSidebar, ServerErrorFallbackCard } from "@/components/ui";
import { AppTopBar } from "@/components/ui/AppTopBar/AppTopBar";
import { GlobalModalManager } from "@/components/ui/GlobalModalManager/GlobalModalManager";
import { FileDuplicationProvider } from "@/components/features/files/duplication/FileDuplicationProvider";
import { ScrollToTopHelper } from "@/components/ui/ScrollToTopHelper/ScrollToTopHelper";
import { UserProvider } from "@/components/UserProvider";
import { getCurrentUserOptions } from "@/lib/data/react-query";
import { redirectIfNotAuthenticated } from "@/lib/utils/auth";
import { cn } from "@/lib/utils/cn";
import { RightPanelProvider } from "@/providers/RightPanelProvider";
import { ErrorBoundary } from "@sentry/react";
import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
  type ErrorComponentProps,
} from "@tanstack/react-router";

class NoFirmIdError extends Error {}

export const Route = createFileRoute("/_auth")({
  component: RouteComponent,
  beforeLoad: async ({ context, location }) => {
    redirectIfNotAuthenticated({ session: context.session, location });
    const currentUserData = await context.queryClient.ensureQueryData(
      getCurrentUserOptions(),
    );

    if (currentUserData.data.firm.onboardingProgress !== "COMPLETED") {
      throw redirect({ to: "/onboarding" });
    }
  },
  errorComponent: ErrorComponent,
});

function RouteComponent() {
  return (
    <ErrorBoundary>
      <UserProvider>
        <FileDuplicationProvider>
          <RightPanelProvider>
            <AuthenticatedAppLayout />
          </RightPanelProvider>
        </FileDuplicationProvider>
      </UserProvider>
    </ErrorBoundary>
  );
}

function AuthenticatedAppLayout() {
  const pathname = useLocation({ select: (loc) => loc.pathname });
  const isMattersRoute = pathname.startsWith("/matters");
  const isAiRoute = pathname === "/ai";

  return (
    <>
      <AppSidebar />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <AppTopBar />
        <div className="relative flex min-h-0 flex-1">
          <main
            className={cn(
              "border-sidebar-border relative z-10 min-w-0 flex-1 rounded-tl-2xl border-t border-l",
              isAiRoute
                ? "overflow-hidden"
                : isMattersRoute
                  ? "overflow-x-hidden overflow-y-auto"
                  : "overflow-auto",
              "bg-background",
            )}
          >
            <Outlet />
          </main>
        </div>
      </div>
      <ScrollToTopHelper />
      <GlobalModalManager />
    </>
  );
}

function ErrorComponent({ error }: ErrorComponentProps) {
  if (error instanceof NoFirmIdError) {
    return (
      <div className="w-full p-4">
        <ServerErrorFallbackCard
          message="Your account has no firm associated with it. It is possible that the
      servers are down; please try again later and if the error persists,
      contact LawWorks Support."
        />
      </div>
    );
  }
  return (
    <div className="w-full p-4">
      <ServerErrorFallbackCard />
    </div>
  );
}
